import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { isImageUrl, normalizeImageUrl, tokenizeBody } from "@/lib/media";
import { Send, Plus, Users, Hash, Image as ImageIcon, X, ArrowLeft, Search } from "lucide-react";

export default function Messaging() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [attachment, setAttachment] = useState("");
  const [showGifPicker, setShowGifPicker] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [typers, setTypers] = useState([]);
  const bottomRef = useRef();
  const typingSentRef = useRef(0);

  const loadChannels = () => api.get("/channels").then(({ data }) => setChannels(data));

  useEffect(() => {
    api.get("/users").then(({ data }) => setUsers(data));
    loadChannels();
  }, []);

  // Deep-link: /messages?dm=<userId> starts or opens a direct message with that user
  useEffect(() => {
    const dm = searchParams.get("dm");
    if (!dm || !user || users.length === 0) return;
    if (dm === user.id) {
      setSearchParams({}, { replace: true });
      return;
    }
    (async () => {
      const existing = channels.find(
        (c) => c.is_direct && c.member_ids?.length === 2 && c.member_ids.includes(dm) && c.member_ids.includes(user.id)
      );
      if (existing) {
        setActive(existing);
      } else {
        const other = users.find((u) => u.id === dm);
        if (!other) return;
        const { data } = await api.post("/channels", { name: `DM: ${other.name}`, member_ids: [dm], is_direct: true });
        await loadChannels();
        setActive(data);
      }
      setSearchParams({}, { replace: true });
    })();
  }, [searchParams, user, users, channels]);

  useEffect(() => {
    if (!active) return;
    const fetchMsgs = () => api.get(`/channels/${active.id}/messages`).then(({ data }) => setMessages(data));
    const fetchTypers = () => api.get(`/channels/${active.id}/typing`).then(({ data }) => setTypers(data.typers || [])).catch(() => {});
    const markRead = () => api.post(`/channels/${active.id}/read`).catch(() => {});
    fetchMsgs();
    fetchTypers();
    markRead();
    const t = setInterval(() => { fetchMsgs(); fetchTypers(); markRead(); }, 3000);
    return () => { clearInterval(t); setTypers([]); };
  }, [active]);

  // Presence heartbeat is handled by Layout — remove duplicate.

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if ((!text.trim() && !attachment) || !active) return;
    const { data } = await api.post("/messages", {
      channel_id: active.id,
      body: text,
      attachment_url: attachment ? normalizeImageUrl(attachment) : null,
    });
    setMessages((m) => [...m, data]);
    setText("");
    setAttachment("");
    setShowGifPicker(false);
  };

  const startDM = async (otherId) => {
    const other = users.find((u) => u.id === otherId);
    if (!other) {
      toast.error("Couldn't find that teammate — refresh and try again.");
      return;
    }
    try {
      const { data } = await api.post("/channels", {
        name: `DM: ${other.name}`,
        member_ids: [otherId],
        is_direct: true,
      });
      await loadChannels();
      setActive(data);
      setShowNew(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const createChannel = async (name, memberIds) => {
    try {
      const { data } = await api.post("/channels", { name, member_ids: memberIds, is_direct: false });
      await loadChannels();
      setActive(data);
      setShowNew(false);
      toast.success(`Group "${data.name}" created`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const userById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  return (
    <div
      className="messaging-shell -mx-4 -mt-4 lg:mx-0 lg:mt-0 h-[calc(100dvh-9rem)] lg:h-[calc(100vh-9rem)] card-surface overflow-hidden flex rounded-none lg:rounded-2xl border-x-0 lg:border-x"
      data-testid="messaging-page"
    >
      <div className={`${active ? "hidden lg:flex" : "flex"} w-full lg:w-72 border-r border-[#2a3447] flex-col min-h-0`}>
        <div className="p-4 flex items-center justify-between border-b border-[#2a3447] shrink-0">
          <h2 className="font-bold">Conversations</h2>
          <button
            onClick={() => setShowNew(true)}
            data-testid="new-conversation"
            className="w-9 h-9 rounded-lg text-black flex items-center justify-center hover:opacity-90 active:scale-95 transition-transform"
            style={{ background: "var(--accent)" }}
            aria-label="Start new conversation"
          >
            <Plus size={16} />
          </button>
        </div>
        <div className="overflow-y-auto overscroll-contain flex-1 p-2">
          {channels.length === 0 && (
            <div className="text-sm text-slate-500 p-3">Start a conversation with a teammate.</div>
          )}
          {channels.map((c) => {
            const otherId = c.is_direct ? c.member_ids.find((i) => i !== user.id) : null;
            const label = c.is_direct ? userById[otherId]?.name || c.name : c.name;
            return (
              <button
                key={c.id}
                onClick={() => setActive(c)}
                data-testid={`channel-${c.id}`}
                className={`w-full text-left p-3 rounded-lg flex items-center gap-2 hover:bg-[#1a202c] active:bg-[#1a202c] ${
                  active?.id === c.id ? "bg-[#1a202c]" : "text-slate-200"
                }`}
                style={active?.id === c.id ? { color: "var(--accent)" } : {}}
              >
                {c.is_direct ? <Users size={14} /> : <Hash size={14} />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{label}</div>
                  {c.last_message && <div className="text-xs text-slate-500 truncate">{c.last_message}</div>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className={`${active ? "flex" : "hidden lg:flex"} flex-1 flex-col min-w-0 min-h-0`}>
        {!active ? (
          <div className="flex-1 flex items-center justify-center text-slate-500">Select a conversation</div>
        ) : (
          <>
            <div className="h-14 shrink-0 border-b border-[#2a3447] flex items-center px-3 lg:px-6 gap-2 bg-[#0b0d12]/80 backdrop-blur-sm">
              <button
                type="button"
                onClick={() => setActive(null)}
                data-testid="back-to-channels"
                aria-label="Back to conversations"
                className="lg:hidden -ml-1 p-2 rounded-lg text-slate-300 hover:text-[color:var(--accent)] hover:bg-[#1a202c] active:scale-90 transition-all"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="font-bold truncate flex items-center gap-2">
                {active.is_direct ? <Users size={16} className="text-slate-400 shrink-0" /> : <Hash size={16} className="text-slate-400 shrink-0" />}
                <span className="truncate">
                  {active.is_direct
                    ? userById[active.member_ids.find((i) => i !== user.id)]?.name || active.name
                    : active.name}
                </span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto overscroll-contain p-3 lg:p-6 space-y-3">
              {messages.map((m) => (
                <MessageBubble key={m.id} m={m} mine={m.sender_id === user.id} viewerId={user.id} channelMemberIds={active.member_ids || []} />
              ))}
              {typers.length > 0 && (
                <div className="flex items-center gap-2 text-xs text-slate-400" data-testid="typing-indicator">
                  <span className="inline-flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" />
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" style={{ animationDelay: "150ms" }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-[color:var(--accent)] animate-pulse" style={{ animationDelay: "300ms" }} />
                  </span>
                  <span>{typers.map((t) => t.user_name).join(", ")} {typers.length === 1 ? "is" : "are"} typing…</span>
                </div>
              )}
              <div ref={bottomRef} />
            </div>

            {attachment && (
              <div className="px-3 pt-2 lg:px-4 lg:pt-3 shrink-0">
                <div className="inline-flex items-center gap-2 bg-[#0b0d12] border border-[#2a3447] rounded-lg p-2" data-testid="attachment-preview">
                  <img src={attachment} alt="attachment preview" className="h-16 rounded" />
                  <button onClick={() => setAttachment("")} data-testid="clear-attachment" className="text-slate-400 hover:text-[#ff3b5c] p-1" aria-label="Remove attachment">
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}

            <form
              onSubmit={send}
              className="shrink-0 p-2.5 lg:p-4 border-t border-[#2a3447] flex gap-2 relative bg-[#0b0d12]/80 backdrop-blur-sm"
              style={{ paddingBottom: "max(0.625rem, env(safe-area-inset-bottom))" }}
            >
              <button
                type="button"
                onClick={() => setShowGifPicker((v) => !v)}
                data-testid="gif-toggle"
                title="Search GIFs"
                aria-label="Open GIF picker"
                className="shrink-0 w-11 h-11 rounded-lg border border-[#2a3447] text-slate-300 hover:text-[color:var(--accent)] hover:border-[color:var(--accent)] active:scale-90 transition-all flex items-center justify-center"
              >
                <ImageIcon size={18} />
              </button>
              <input
                data-testid="message-input"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  if (active && Date.now() - typingSentRef.current > 3000) {
                    typingSentRef.current = Date.now();
                    api.post(`/channels/${active.id}/typing`).catch(() => {});
                  }
                }}
                className="flex-1 min-w-0 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 lg:px-4 py-2.5 outline-none focus:border-[color:var(--accent)] text-base"
                placeholder="Type a message…"
                autoComplete="off"
                autoCorrect="on"
                enterKeyHint="send"
              />
              <button
                type="submit"
                data-testid="send-message"
                aria-label="Send message"
                disabled={!text.trim() && !attachment}
                className="shrink-0 w-11 h-11 lg:w-auto lg:px-4 lg:h-auto lg:py-2.5 rounded-lg text-black font-bold hover:opacity-90 active:scale-90 transition-all flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "var(--accent)" }}
              >
                <Send size={18} /> <span className="hidden lg:inline">Send</span>
              </button>

              {showGifPicker && (
                <GifPicker
                  onPick={(url) => { setAttachment(url); setShowGifPicker(false); }}
                  onClose={() => setShowGifPicker(false)}
                />
              )}
            </form>
          </>
        )}
      </div>

      {showNew && (
        <NewConversationModal
          users={users.filter((u) => u.id !== user.id)}
          onDM={startDM}
          onGroup={createChannel}
          onClose={() => setShowNew(false)}
        />
      )}
    </div>
  );
}

function MessageBubble({ m, mine, viewerId, channelMemberIds }) {
  const tokens = tokenizeBody(m.body);
  const hasInlineImg = tokens.some((t) => t.type === "image");
  const attachmentUrl = m.attachment_url && isImageUrl(m.attachment_url) ? m.attachment_url : null;
  // Read receipts: only surface on the sender's own messages (their audience is everyone else).
  const readers = (m.read_by || []).filter((r) => r.user_id !== m.sender_id);
  const totalOtherMembers = Math.max(0, (channelMemberIds || []).filter((id) => id !== m.sender_id).length);
  const seenBy = readers.length;
  const allSeen = totalOtherMembers > 0 && seenBy >= totalOtherMembers;
  const readerNames = readers.map((r) => r.user_name).join(", ") || "No one else yet";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
      <div className="max-w-md flex flex-col">
        <div
          className={`px-4 py-2.5 rounded-2xl text-sm ${
            mine ? "text-black rounded-br-sm" : "bg-[#1a202c] text-slate-100 border border-[#2a3447] rounded-bl-sm"
          }`}
          style={mine ? { background: "var(--accent)" } : {}}
        >
          {!mine && <div className="text-[10px] font-bold text-[#00e5ff] mb-1">{m.sender_name}</div>}
          {tokens.length > 0 && (
            <div className="whitespace-pre-wrap break-words">
              {tokens.map((t, i) =>
                t.type === "image" ? (
                  <img key={`${m.id}-tok-${i}`} src={t.url} alt="Shared GIF" loading="lazy" className="rounded-lg my-1 max-h-64 max-w-full" />
                ) : (
                  <span key={`${m.id}-tok-${i}`}>{t.value}</span>
                ),
              )}
            </div>
          )}
          {attachmentUrl && (
            <img src={attachmentUrl} alt="Attached GIF" loading="lazy" className={`rounded-lg max-h-64 max-w-full ${(m.body || "").trim() && !hasInlineImg ? "mt-2" : ""}`} />
          )}
        </div>
        {mine && (
          <div className={`text-[10px] uppercase tracking-widest mt-1 flex justify-end gap-1 ${allSeen ? "text-[color:var(--accent)]" : "text-slate-500"}`} title={`Read by: ${readerNames}`} data-testid={`read-${m.id}`}>
            <span>{allSeen ? "Seen" : seenBy > 0 ? `Seen · ${seenBy}` : "Sent"}</span>
            {totalOtherMembers > 1 && seenBy > 0 && <span>({seenBy}/{totalOtherMembers})</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function GifPicker({ onPick, onClose }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const search = async (query) => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get("/gifs/search", { params: { q: query || "", limit: 24 } });
      setRows(data || []);
    } catch (e) {
      setRows([]);
      setErr(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  // Load trending on open, then debounce search-as-you-type.
  useEffect(() => { search(""); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  useEffect(() => {
    const t = setTimeout(() => search(q.trim()), 350);
    return () => clearTimeout(t);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [q]);

  return (
    <>
      {/* Mobile backdrop — closes the sheet when tapped */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Close GIF picker"
        className="md:hidden fixed inset-0 z-20 bg-black/60 backdrop-blur-sm"
      />
      <div
        className="fixed left-0 right-0 bottom-0 z-30 rounded-t-2xl md:rounded-2xl bg-[#0b0d12] border-t md:border border-[#2a3447] p-3 md:p-4 md:absolute md:left-3 md:right-3 md:bottom-16 shadow-2xl gif-picker-sheet"
        data-testid="gif-picker"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      >
        <div className="md:hidden w-10 h-1 rounded-full bg-slate-600/60 mx-auto mb-2" aria-hidden />
        <div className="flex items-center justify-between mb-3 gap-2">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search GIPHY…"
              data-testid="gif-search-input"
              className="w-full pl-9 pr-3 py-2 text-base bg-[#141821] border border-[#2a3447] rounded-lg outline-none focus:border-[color:var(--accent)]"
            />
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200 p-2 active:scale-90 transition-transform" data-testid="gif-close" aria-label="Close GIF picker">
            <X size={18} />
          </button>
        </div>
        <div className="max-h-[45vh] md:max-h-72 overflow-y-auto overscroll-contain">
          {loading && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="w-full h-24 rounded-lg bg-[#1a202c] animate-pulse" />
              ))}
            </div>
          )}
          {!loading && err && (
            <div className="text-xs text-[#ff3b5c] p-3 text-center" data-testid="gif-error">{err}</div>
          )}
          {!loading && !err && rows.length === 0 && (
            <div className="text-xs text-slate-500 p-6 text-center">No GIFs found. Try a different search.</div>
          )}
          {!loading && !err && rows.length > 0 && (
            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
              {rows.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => onPick(g.url)}
                  data-testid={`gif-item-${g.id}`}
                  title={g.title}
                  className="rounded-lg overflow-hidden border border-[#2a3447] hover:border-[color:var(--accent)] active:scale-95 transition-all bg-[#0b0d12]"
                >
                  <img src={g.preview_url} alt={g.title} loading="lazy" className="w-full h-24 object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mt-3 pt-2 border-t border-[#2a3447] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-slate-500">Powered by GIPHY</span>
          <span className="text-[10px] text-slate-500">Tap a GIF to attach</span>
        </div>
      </div>
    </>
  );
}

function NewConversationModal({ users, onDM, onGroup, onClose }) {
  const [mode, setMode] = useState("dm");
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);

  const canSubmit =
    mode === "dm" ? selected.length === 1 : name.trim().length > 0 && selected.length > 0;

  const submit = () => {
    if (mode === "dm") {
      if (!selected[0]) {
        toast.error("Pick a teammate first");
        return;
      }
      onDM(selected[0]);
      return;
    }
    if (!name.trim()) {
      toast.error("Give the group a name");
      return;
    }
    if (!selected.length) {
      toast.error("Add at least one teammate to the group");
      return;
    }
    onGroup(name.trim(), selected);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card-surface p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="new-conversation-modal">
        <div className="flex gap-2 mb-4">
          {["dm", "group"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setSelected([]); }}
              className={`px-4 py-2 rounded-lg text-xs uppercase tracking-widest ${
                mode === m ? "text-black" : "border border-[#2a3447] text-slate-300"
              }`}
              style={mode === m ? { background: "var(--accent)" } : {}}
              data-testid={`mode-${m}`}
            >
              {m === "dm" ? "Direct" : "Group"}
            </button>
          ))}
        </div>
        {mode === "group" && (
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Channel name"
            data-testid="channel-name-input"
            className="w-full mb-3 bg-[#0b0d12] border border-[#2a3447] rounded-lg px-3 py-2 outline-none focus:border-[color:var(--accent)]"
          />
        )}
        <div className="max-h-60 overflow-auto space-y-1">
          {users.length === 0 && (
            <div className="text-xs text-slate-500 p-3 text-center">No teammates found. Invite people first.</div>
          )}
          {users.map((u) => (
            <label key={u.id} className="flex items-center gap-2 p-2 hover:bg-[#1a202c] rounded-lg cursor-pointer">
              <input
                type={mode === "dm" ? "radio" : "checkbox"}
                name="user"
                checked={selected.includes(u.id)}
                onChange={() => {
                  if (mode === "dm") setSelected([u.id]);
                  else setSelected((s) => (s.includes(u.id) ? s.filter((i) => i !== u.id) : [...s, u.id]));
                }}
                data-testid={`pick-user-${u.id}`}
                className="accent-[color:var(--accent)]"
              />
              <span className="text-sm">{u.name} <span className="text-slate-500">· {u.title}</span></span>
            </label>
          ))}
        </div>
        {mode === "group" && (
          <div className="mt-2 text-[11px] text-slate-500">
            {selected.length} teammate{selected.length === 1 ? "" : "s"} selected
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-400 hover:text-slate-200" data-testid="new-conversation-cancel">Cancel</button>
          <button
            onClick={submit}
            disabled={!canSubmit}
            data-testid="create-conversation"
            className="px-4 py-2 rounded-lg text-black font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: "var(--accent)" }}
          >
            {mode === "dm" ? "Start chat" : "Create group"}
          </button>
        </div>
      </div>
    </div>
  );
}
